import legalEntityMoreInformationSchema from '../schemas/legalEntity/legalEntityMoreInformationSchema'
import citizenMoreInformationSchema from '../schemas/citizen/citizenMoreInformationSchema'
import immunobiologicalInformationSchema from '../schemas/immunobiological/immunobiologicalInformationSchema'
import homeVisitInformationSchema from '../schemas/homeVisit/homeVisitInformationSchema'
import attendanceInformationSchema from '../schemas/attendance/attendanceInformationSchema'
import prescribedDrugsInformationSchema from '../schemas/prescribedDrugs/prescribedDrugsInformationSchema'
import medicalProcedureInformationSchema from '../schemas/medicalProcedure/medicalProcedureInformationSchema'
import healthTeamInformationSchema from '../schemas/healthTeam/healthTeamInformationSchema'
import healthUnitInformationSchema from '../schemas/healthUnit/healthUnitInformationSchema'
import healthProfessionalInformationSchema from '../schemas/healthProfessional/healthProfessionalInformationSchema'

// Mapeamento de todas as classes de schema disponíveis
const schemaMap = {
  legalEntityMoreInformationSchema,
  citizenMoreInformationSchema,
  immunobiologicalInformationSchema,
  homeVisitInformationSchema,
  attendanceInformationSchema,
  prescribedDrugsInformationSchema,
  medicalProcedureInformationSchema,
  healthTeamInformationSchema,
  healthUnitInformationSchema,
  healthProfessionalInformationSchema
}

/**
 * Resolve o nome base do schema baseado em padrões conhecidos
 * @param key Chave da variável (ex: immunobiological_BCG)
 * @returns Nome base do schema (ex: immunobiological)
 */
export function resolveBaseSchemaName(key: string): string {
  // Define padrões conhecidos para agrupar variáveis similares
  const patterns = [
    { pattern: /^immunobiological/, base: 'immunobiological' },
    { pattern: /^homeVisit/, base: 'homeVisit' },
    { pattern: /^attendanceMedicalProcedure/, base: 'medicalProcedure' },
    { pattern: /^attendance/, base: 'attendance' },
    { pattern: /^citizen/, base: 'citizen' },
    { pattern: /^legalEntity/, base: 'legalEntity' },
    { pattern: /^prescribedDrugs/, base: 'prescribedDrugs' },
    { pattern: /^medicalProcedure/, base: 'medicalProcedure' },
    { pattern: /^healthTeam/, base: 'healthTeam' },
    { pattern: /^healthUnit/, base: 'healthUnit' },
    { pattern: /^healthProfessional/, base: 'healthProfessional' },
  ]

  // Encontra o padrão correspondente
  for (const { pattern, base } of patterns) {
    if (pattern.test(key)) {
      return base
    }
  }

  // Se não encontrar padrão, retorna a própria key
  return key
}

/**
 * Constrói o nome completo do schema baseado na key e sufixo
 * @param variableKey Chave da variável
 * @param schemaSuffix Sufixo do schema (ex: 'InformationSchema', 'MoreInformationSchema')
 * @returns Nome completo do schema
 */
export function buildSchemaName(variableKey: string, schemaSuffix: string): string {
  const baseName = resolveBaseSchemaName(variableKey)
  return `${baseName}${schemaSuffix}`
}

/**
 * Helper para obter um schema específico ou retornar um fallback
 * @param expectedClassName Nome da classe esperada do schema
 * @param fallback Schema de fallback caso a classe esperada não seja encontrada
 * @param variableKey Chave da variável para resolução automática
 * @returns O schema da classe esperada ou o fallback
 */
export function getSchemaOrFallback<T>(
  expectedClassName: string,
  fallback: T,
  variableKey?: string,
): T | (typeof schemaMap)[keyof typeof schemaMap] {
  // Primeiro tenta com o nome exato fornecido
  if (expectedClassName in schemaMap) {
    return schemaMap[expectedClassName as keyof typeof schemaMap]
  }

  // Se variableKey foi fornecida, tenta resolução automática
  if (variableKey) {
    // Extrai o sufixo do schema (ex: 'InformationSchema' de 'violenceInformationSchema')
    const suffixMatch = expectedClassName.match(/([A-Z][a-z]+Schema)$/)
    if (suffixMatch) {
      const suffix = suffixMatch[1]
      const resolvedSchemaName = buildSchemaName(variableKey, suffix)

      if (resolvedSchemaName in schemaMap) {
        return schemaMap[resolvedSchemaName as keyof typeof schemaMap]
      }
    }
  }

  // Retorna o fallback se nenhuma resolução funcionar
  return fallback
}

/**
 * Verifica se um schema específico existe no mapeamento
 * @param expectedClassName Nome da classe esperada do schema
 * @param variableKey Chave da variável para resolução automática
 * @returns Verdadeiro se o schema existir, falso caso contrário
 */
export function hasSchema(expectedClassName: string, variableKey?: string): boolean {
  // Verifica se existe com nome exato
  if (expectedClassName in schemaMap) {
    return true
  }

  // Se variableKey foi fornecida, tenta resolução automática
  if (variableKey) {
    const suffixMatch = expectedClassName.match(/([A-Z][a-z]+Schema)$/)
    if (suffixMatch) {
      const suffix = suffixMatch[1]
      const resolvedSchemaName = buildSchemaName(variableKey, suffix)
      return resolvedSchemaName in schemaMap
    }
  }

  return false
}

/**
 * Resolve o título da seção baseado na chave e título da variável
 * @param key Chave da definição da ação
 * @param variableTitle Título da variável associada
 * @returns Título resolvido para a seção
 */
export const resolveSectionTitle = (key: string, variableTitle?: string) => {
  const baseName = resolveBaseSchemaName(key)

  // Mapeamento de títulos baseado no nome base
  const baseTitles: Record<string, string> = {
    immunobiological: 'Sobre a imunização',
    homeVisit: 'Sobre a visita domiciliar',
    attendance: 'Sobre o atendimento',
    citizen: 'Sobre o cidadão',
    legalEntity: 'Sobre a instituição',
    prescribedDrugs: 'Sobre a prescrição',
    medicalProcedure: 'Sobre o procedimento',
    healthTeam: 'Sobre a equipe de saúde',
    healthUnit: 'Sobre a unidade de saúde',
    healthProfessional: 'Sobre o profissional de saúde',
  }

  // Para immunobiological com sufixos específicos, usa título personalizado
  if (key.startsWith('immunobiological_') && variableTitle) {
    return `Sobre a vacinação`
  }

  // Usa o título base se disponível
  if (baseTitles[baseName]) {
    return baseTitles[baseName]
  }

  // Schema-based fallback
  if (hasSchema(`${key}InformationSchema`, key)) {
    return `Informações de ${variableTitle?.toLowerCase() || 'dados'}`
  }

  if (variableTitle) {
    return `Informações de ${variableTitle.toLowerCase()}`
  }

  return 'Localização'
}

export default getSchemaOrFallback
